import type { AIAnalysisResult } from '$lib/types/ai';

/**
 * Grammar-specific response parser for AI providers
 */
export class GrammarResponseParser {
  /**
   * Parse AI response to extract JSON array
   * Handles various response formats from different providers
   */
  static parseResponse(content: string, providerName: string = 'AI'): AIAnalysisResult[] {
    try {
      // Try direct JSON parsing first
      try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed)) {
          return this.validateResults(parsed);
        }
        // If it's an object, wrap it in an array
        if (typeof parsed === 'object' && parsed !== null) {
          return this.validateResults([parsed]);
        }
      } catch {
        // Direct parsing failed, try other methods
      }
      
      // Extract JSON from code blocks (```json ... ```)
      let jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch && jsonMatch[1]) {
        const parsed = JSON.parse(jsonMatch[1]);
        return this.validateResults(Array.isArray(parsed) ? parsed : [parsed]);
      }
      
      // Find JSON array starting with bracket
      jsonMatch = content.match(/\[[\s\S]*?\]/);
      if (jsonMatch) {
        return this.validateResults(JSON.parse(jsonMatch[0]));
      }
      
      // Try to find JSON object
      jsonMatch = content.match(/\{[\s\S]*?\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return this.validateResults(Array.isArray(parsed) ? parsed : [parsed]);
      }
      
      throw new Error('JSON format not found in response');
    } catch (error) {
      console.error(`[${providerName}] Response parsing failed:`, error);
      console.error(`[${providerName}] Original response:`, content);
      
      // Return empty array instead of throwing to prevent crashes
      return [];
    }
  }

  /**
   * Validate and normalize AI analysis results
   */
  static validateResults(results: any[]): AIAnalysisResult[] {
    if (!Array.isArray(results)) {
      console.warn('Results is not an array, wrapping in array');
      results = [results];
    }

    return results.map((item: any) => ({
      correctionIndex: typeof item.correctionIndex === 'number' ? item.correctionIndex : 0,
      selectedValue: item.selectedValue || '',
      isExceptionProcessed: typeof item.isExceptionProcessed === 'boolean' ? item.isExceptionProcessed : false,
      keepOriginal: typeof item.keepOriginal === 'boolean' ? item.keepOriginal : false,
      reasoning: item.reasoning || ''
    }));
  }

  /**
   * Parse simple message response (for sendMessage method)
   */
  static parseSimpleResponse(data: any, provider: 'openai' | 'anthropic' | 'google' | 'upstage'): string {
    switch (provider) {
      case 'openai':
      case 'upstage':
        return data.choices?.[0]?.message?.content || '';
      
      case 'anthropic':
        return data.content?.[0]?.text || '';
      
      case 'google':
        // Handle Google's response structure
        if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
          return data.candidates[0].content.parts[0].text;
        }
        return '';
      
      default:
        return '';
    }
  }

  /**
   * Extract text content from Google API response
   */
  static parseGoogleResponse(data: any): string {
    // Check if response has the expected structure
    if (!data.candidates || !data.candidates[0]) {
      console.error('[ResponseParser] Unexpected Google response structure:', data);
      throw new Error('Invalid response from Google API');
    }
    
    const candidate = data.candidates[0];
    
    // Check if response has content
    if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
      console.warn('[ResponseParser] Google response has no content parts');
      return '';
    }
    
    // Check if the text content exists
    if (!candidate.content.parts[0].text) {
      console.error('[ResponseParser] Google response part has no text:', candidate.content.parts[0]);
      return '';
    }
    
    return candidate.content.parts[0].text;
  }

  /**
   * Handle error responses from API
   */
  static formatErrorMessage(provider: string, status: number, errorText: string): string {
    let errorMessage = `${provider} API error: ${status}`;
    
    try {
      const errorData = JSON.parse(errorText);
      
      // Handle different error formats
      if (errorData.error?.message) {
        errorMessage += ` - ${errorData.error.message}`;
      } else if (errorData.message) {
        errorMessage += ` - ${errorData.message}`;
      } else if (errorData.detail) {
        errorMessage += ` - ${errorData.detail}`;
      }
    } catch {
      // If not JSON, include first 200 chars of error text
      errorMessage += ` - ${errorText.substring(0, 200)}`;
    }
    
    return errorMessage;
  }
}